/**
 * ------ maintenance history ------
 *
 */

import { Component, OnInit, OnDestroy, ViewChild, HostListener } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { Store, createSelector, select } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { IntralinksAccountDialogService } from './intralinks-account-dialog.service';
import { IntralinksAccountDialogOpenParam, IntralinksAccountDialogOperations } from './intralinks-account-dialog.view-model';
import { DualListConfig, DualListEventType } from '../../widgets/dual-list/dual-list.config';
import { StoreQuerierService } from '../../services/store-querier.service';
import { DualListItem } from '../../widgets/dual-list/dual-list.component';
import { AlertWindowService } from '../../widgets/alert-window/alert-window.service';
import { AppState } from '../../redux';
import { ToastService } from '../../widgets/toast/toast.service';
import { NotificationStyles } from '../../widgets/notification/notification.model';
import { SystemUser } from '../../tamalelibs/models/user.model';
import { businessConstants } from '../../constants/business.constants';
import { IntralinksCreateAction, IntralinksUpdateAction } from '../../tamalelibs/redux/intralinks.actions';
import { IntralinksAccountInfo } from '../../tamalelibs/models/intralinks.model';
import { getPageStatusState } from '../../tamalelibs/redux/intralinks.reducer';
import { AuthService } from '../../tamalelibs/services/auth.service';
import { IntralinksSessionService } from '../../services/intralinks-session.service';
import { StringLiteralsPipe } from '../../pipes/translate.pipe';

@Component({
    selector: 'intralinks-account-dialog',
    templateUrl: './intralinks-account-dialog.component.html',
    styleUrls: ['./intralinks-account-dialog.component.scss']
})
export class IntralinksAccountDialogComponent implements OnInit, OnDestroy {

    @ViewChild('dualList', { static: false }) dualListInst;

    screenId: string = null;
    currentUser: SystemUser;
    dualListConfig: DualListConfig;
    isOpen = false;
    isSaving = false;
    mappings: any;
    pageTitle: string;
    pageType: string;
    saveBtnStatus = false;
    homeScreenData: any; // Add a param for homescreen
    isDisabled = false;
    screenForm = new UntypedFormGroup({
        username: new UntypedFormControl(''),
        password: new UntypedFormControl(''),
        alias: new UntypedFormControl(''),
    });
    endOtherSessions = false;
    originEndOtherSessions = false;

    private _operation: IntralinksAccountDialogOperations;
    private _isManuallyChange = true;
    private _originalUsername: string;
    private _originalPassword: string;
    private _originalAlias: string;

    get username() {
        return this.screenForm.get('username');
    }

    get password() {
        return this.screenForm.get('password');
    }

    get alias() {
        return this.screenForm.get('alias');
    }

    private _destroySubscriptions: Array<Subscription> = [];
    private _hadModifyDualList = false;

    constructor(
        private _dialogService: IntralinksAccountDialogService,
        private _store: Store<AppState>,
        private _storeQuerier: StoreQuerierService,
        private _toast: ToastService,
        private _alertWindow: AlertWindowService,
        private _authService: AuthService,
        private _router: Router,
        private _intralinksSessionService: IntralinksSessionService
    ) { }

    ngOnInit() {
        this.currentUser = this._storeQuerier.getCurrentUser();
        this._intralinksSessionService.addListener();
        this.initComponent();

        const accessListSelector = createSelector(
            (state: AppState) => state.users,
            (state: AppState) => state.teams,
            (allUser, allTeam) => {
                if (allUser.length === 0 && allTeam.length === 0) {
                    return [];
                }
                const users = allUser.map(item => new DualListItem(item));
                const teams = allTeam.map(item => {
                    return new DualListItem(Object.assign({ type: businessConstants.entity.entityType.Team }, item));
                });
                return users.concat(teams);
            }
        );

        this._destroySubscriptions.push(
            this._dialogService.open$.subscribe((param: IntralinksAccountDialogOpenParam) => this._dialogOpenHandler(param)),
            this._store.select(getPageStatusState).subscribe((state) => {
                if (!!state && this.isOpen) { this._createdSuccessHandler(state); }
            })
        );

        this._destroySubscriptions.push(
            this._store.pipe(
                select(accessListSelector),
                filter((list: any) => list.length > 0),
            ).subscribe(list => {
                this.dualListConfig.dataSource = list;
            })
        );
        this._destroySubscriptions.push(
            this.dualListConfig.feedbackSubject$.pipe(
                filter((item) => item.type === DualListEventType.selectChange)
            ).subscribe(() => {
                this._hadModifyDualList = true;
                this.saveBtnStatus = false;
            })
        );
        // handle value change to change the save button status
        this.screenForm.valueChanges.subscribe(data => {
            if (this._isManuallyChange) {
                // data.alias = data.alias.trim();
                this.username.setErrors(null);
                this.password.setErrors(null);
                this.alias.setErrors(null);
                if (data.username !== this._originalUsername ||
                    data.password !== this._originalPassword ||
                    data.alias !== this._originalAlias) {
                    this.saveBtnStatus = false;
                } else {
                    this.saveBtnStatus = true;
                }
            }
        });
    }

    ngOnDestroy() {
        this._destroySubscriptions.forEach(subscription => subscription.unsubscribe());
        this._intralinksSessionService.removeListener();
    }

    initComponent() {
        this.dualListConfig = new DualListConfig();
        this.dualListConfig.required = true;
        this.dualListConfig.titles = [businessConstants.dualList.userNotAllowedAccessTitle, businessConstants.dualList.userAllowedAccessTitle];
        this.dualListConfig.placeholderText = businessConstants.dualList.placeHolderText;
        this.dualListConfig.dataTextField = businessConstants.dualList.dataTextField;
        this.dualListConfig.dataValueField = businessConstants.dualList.dataValueField;
    }

    @HostListener('window:beforeunload', ['$event'])
    beforeunloadHandler($event: any) {
        if (this.isOpen) {
            $event.returnValue = true;
        }
    }

    onCancelClick() {
        if (this._hadModifyDualList || this.screenForm.dirty) {
            const subscription = this._alertWindow.warn(businessConstants.alertWindow.warnTitle, [businessConstants.alertWindow.warnContent])
                .subscribe((result: boolean) => {
                    if (result) {
                        this._closeDialog();
                    }
                    subscription.unsubscribe();
                });
        } else {
            this._closeDialog();
        }
    }

    onSaveClick() {
        this._doValidation();

        if (this.screenForm.invalid) {
            return;
        }

        this.isSaving = true;
        if (this._operation === IntralinksAccountDialogOperations.CREATE) {
            const action = new IntralinksCreateAction();
            action.payload = new IntralinksAccountInfo(
                this.username.value,
                this._authService.encryptPassword(this.password.value),
                this.alias.value,
                this.dualListInst.getSelectedItem(true)
            );
            action.payload.endOtherSessions = !!this.endOtherSessions;
            action.payload.folderMappings = this.mappings;
            this._store.dispatch(action);
        } else {
            const action = new IntralinksUpdateAction();
            action.payload = new IntralinksAccountInfo(
                this.username.value,
                this._authService.encryptPassword(this.password.value),
                this.alias.value,
                this.dualListInst.getSelectedItem(true)
            );
            action.payload.endOtherSessions = !!this.endOtherSessions;
            action.payload.folderMappings = this.mappings;
            action.screenId = this.screenId;
            this._store.dispatch(action);
        }
    }

    private _closeDialog() {
        this.isOpen = false;
        this._reset();
    }

    private _doValidation() {
        this.username.setErrors(null);
        this.password.setErrors(null);

        if (!this.username.value) {
            this.username.setErrors({
                required: true
            });
        }
        if (!this.password.value) {
            this.password.setErrors({
                required: true
            });
        }
        if (!this.alias.value) {
            this.alias.setErrors({
                required: true
            });
        }
    }

    private _dialogOpenHandler(param: IntralinksAccountDialogOpenParam) {
        if (param.operation === IntralinksAccountDialogOperations.CREATE) {
            this.screenId = null;
            this.saveBtnStatus = false;
            this.pageTitle = businessConstants.intralinkScreen.accountDialog.createTitle;
            this.pageType = businessConstants.screenType.intralink;
            this.username.setValue('');
            this.password.setValue('');
            this.alias.setValue('');
            this.endOtherSessions = false;
            this.originEndOtherSessions = false;
            this.mappings = null;
            this.isDisabled = false;

            setTimeout(() => {
                this.dualListInst.setSelectedItem([this.currentUser.id]);
            });
        } else if (param.operation === IntralinksAccountDialogOperations.EDIT) {
            this.screenId = param.screenId;
            this.saveBtnStatus = true;
            this._isManuallyChange = false;
            this.pageTitle = businessConstants.intralinkScreen.accountDialog.editTitle;
            this.pageType = businessConstants.screenType.intralink;
            this._originalUsername = param.data.username;
            this._originalPassword = param.data.password;
            this._originalAlias = param.data.alias;
            this.originEndOtherSessions = param.data.endOtherSessions;
            this.username.setValue(param.data.username);
            this.password.setValue(this._authService.decryptPassword(param.data.password));
            this.alias.setValue(param.data.alias);
            this.endOtherSessions = param.data.endOtherSessions;
            this.mappings = param.data.folderMappings;
            this.isDisabled = false;

            setTimeout(() => {
                if (param.data.recipients) {
                    this.dualListInst.setSelectedItem(param.data.recipients);
                }
                this._isManuallyChange = true;
            });
        }
        this._operation = param.operation;
        this.isOpen = true;
    }

    private _reset() {
        this._hadModifyDualList = false;
        this.saveBtnStatus = true;
        this.endOtherSessions = false;
        this.originEndOtherSessions = false;
        this.screenForm.reset();
        this.dualListConfig.dataSource.forEach(item => {
            item.direction = businessConstants.common.position.left;
        });
    }

    private _failedHandler(error) {
        if (error.message.includes(businessConstants.errorMessage.ERR_NAME_EXISTED)) {
            if (error.message.includes('account name')) {
                this.username.setErrors({
                    duplicated: true
                });
            } else {
                this.alias.setErrors({
                    duplicated: true
                });
            }
        } else if (error.message.includes(businessConstants.errorMessage.ERR_FIRM_SETTING_ERROR)) {
            this.password.setErrors({
                errorValue: true
            });
            this.username.setErrors({
                errorValue: true
            });
        } else {
            let errorMessage = error.message;
            if (errorMessage.includes(businessConstants.errorMessage.ERR_ARGUMENT_INVALID)) {
                errorMessage = StringLiteralsPipe.translate('general.username_and_password_is_not_recognized');

            } else if (errorMessage.includes(businessConstants.errorMessage.ERR_INTRALINKS_SERVER)) {
                errorMessage = StringLiteralsPipe.translate('general.the_configuration_is_not_correct');
            }
            this._toast.notify({
                message: errorMessage,
                style: NotificationStyles.Error
            });
        }
    }

    private _createdSuccessHandler(response) {
        // need to confirm with yoyo about this fix
        this.isSaving = false;
        let msg = '';
        const msgType = NotificationStyles.Success;
        switch (response.workflow) {
            case 'add':
                msg = businessConstants.notification.intralinks.createSuccessfullyMsg;
                this._closeDialog();
                this._toast.notify({
                    message: response.name + msg,
                    style: msgType
                });
                this._router.navigate(['feeds', 'intralinks', response.id]);
                break;
            case 'edit':
                msg = businessConstants.notification.intralinks.editSuccessfullyMsg;
                this._closeDialog();
                this._toast.notify({
                    message: response.name + msg,
                    style: msgType
                });
                break;
            case 'add_fail':
            case 'edit_fail':
                this._failedHandler(response.error);
                break;
            case 'fail':
                this.endOtherSessions = this.originEndOtherSessions;
                break;
        }
    }
}
